
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CreditCard, Lock, Loader2 } from "lucide-react";
import { PricingPlan } from "@/hooks/useSubscription";
import { toast } from "sonner";
import SignInModal from "@/components/auth/SignInModal";

interface SubscribeButtonProps {
  plan: PricingPlan;
  user: any;
  isCurrentPlan: (planId: string) => boolean;
  handleSubscribe: (planId: string) => void;
  loading: boolean;
  className?: string;
}

export default function SubscribeButton({ 
  plan, 
  user, 
  isCurrentPlan, 
  handleSubscribe, 
  loading,
  className 
}: SubscribeButtonProps) {
  const navigate = useNavigate();
  const [showSignInModal, setShowSignInModal] = useState(false);
  const isPopular = plan.type === "yearly";

  const onClick = () => {
    if (!user) {
      setShowSignInModal(true);
      return;
    }
    
    try {
      toast.info("Preparing checkout...");
      handleSubscribe(plan.id);
    } catch (error: any) {
      const errorMessage = error?.message || "Failed to process subscription. Please try again later.";
      toast.error(errorMessage);
      console.error("Subscription error:", error);
    }
  };

  const handleSignInSuccess = () => {
    // After successful sign in, trigger the subscription process
    setTimeout(() => {
      try {
        toast.info("Preparing checkout...");
        handleSubscribe(plan.id);
      } catch (error: any) {
        const errorMessage = error?.message || "Failed to process subscription. Please try again later.";
        toast.error(errorMessage);
        console.error("Subscription error:", error);
      }
    }, 500); // Small delay to ensure auth state is updated
  };

  return (
    <>
      <Button
        variant={isPopular ? "default" : "outline"}
        className={className}
        disabled={user && isCurrentPlan(plan.id) || loading}
        onClick={onClick}
      >
        {loading ? (
          <>
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            {!user && (
              <Lock className="mr-1.5 h-4 w-4" />
            )}
            {user && isCurrentPlan(plan.id) ? "Current Plan" : "Subscribe"}
            {plan.type !== "free" && (
              <CreditCard className="ml-1.5 h-4 w-4" />
            )}
          </>
        )}
      </Button>
      <SignInModal
        isOpen={showSignInModal}
        onClose={() => setShowSignInModal(false)}
        onSuccess={handleSignInSuccess}
      />
    </>
  );
}
